import { ApiRequestOptions, ApiClientError } from "@interfaces";
import { ApiResponse } from "@bbb/model";
import { getPostHogRequestHeaders } from "@lib/analytics/posthog";

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
let csrfTokenOverride: string | null = null;

export function setCsrfToken(token: string | null | undefined) {
  csrfTokenOverride = token?.trim() || null;
}

export async function _apiRequest<T>(
  path: string,
  {
    method = "GET",
    data,
    token: _legacyToken,
    signal,
    headers,
    params,
    authRequired: _authRequired = true,
  }: ApiRequestOptions = {}
): Promise<T> {
  const url = `${API_BASE_URL}${path}${buildQueryString(params)}`;
  const requestHeaders = new Headers(headers);
  Object.entries(getPostHogRequestHeaders()).forEach(([key, value]) => {
    if (!requestHeaders.has(key)) {
      requestHeaders.set(key, value);
    }
  });

  // Add if missing json header
  if (
    data &&
    !(data instanceof FormData) &&
    !requestHeaders.has("Content-Type")
  ) {
    requestHeaders.set("Content-Type", "application/json");
  }

  const csrfToken = getCookie("bbb_csrf") ?? csrfTokenOverride;
  if (csrfToken && UNSAFE_METHODS.has(method.toUpperCase())) {
    requestHeaders.set("x-csrf-token", csrfToken);
  }

  const response = await fetch(url, {
    method,
    signal,
    headers: requestHeaders,
    credentials: "include",
    body: data instanceof FormData ? data : JSON.stringify(data),
  });

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const message =
      (isJson && typeof payload === "object" && payload?.message) ||
      response.statusText ||
      "Request failed";

    throw new ApiClientError(
      message,
      response.status,
      isJson && typeof payload === "object" ? payload?.details : undefined
    );
  }

  return unwrapApiResponse<T>(payload);
}

//#region PRIVATE HELPERS
export const API_BASE_URL = (() => {
  const raw = import.meta.env.VITE_DASHBOARD_API_BASE_URL;
  if (!raw) {
    throw new Error(
      "VITE_DASHBOARD_API_BASE_URL is not defined. Please set it in your environment."
    );
  }

  return raw.replace(/\/$/, "");
})();

function buildQueryString(
  params?: Record<string, string | number | boolean | null | undefined>
): string {
  if (!params) return "";
  const filtered = Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      acc[key] = String(value);
    }
    return acc;
  }, {} as Record<string, string>);

  const query = new URLSearchParams(filtered).toString();
  return query ? `?${query}` : "";
}

function unwrapApiResponse<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === "object" &&
    "data" in (payload as ApiResponse<T>)
  ) {
    return (payload as ApiResponse<T>).data;
  }
  return payload as T;
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  const match = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : null;
}
//#endregion PRIVATE HELPERS
